import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useMoneyFmt, useDateFmt } from "@/lib/company";
import { useCompany } from "@/lib/company";
import { TID } from "@/constants/testIds";
import { Plus, Trash2, X, AlertTriangle, Pencil, Repeat, Check, Package, Sparkles, Send, Loader2, MailWarning, Clock, ChevronDown, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { useCreateListener, useActionListener } from "@/lib/createBus";
import MonthCloseBreadcrumb from "@/components/MonthCloseBreadcrumb";
import MemorizeModal from "@/components/MemorizeModal";
import ItemPicker from "@/components/ItemPicker";

const BUCKETS = [
  { key: "current", label: "Current", desc: "Not yet due", color: "emerald" },
  { key: "1_30", label: "1–30 days", desc: "Past due", color: "amber" },
  { key: "31_60", label: "31–60 days", desc: "Late", color: "orange" },
  { key: "61_90", label: "61–90 days", desc: "Very late", color: "red" },
  { key: "over_90", label: "90+ days", desc: "Critical", color: "rose" },
];

const BAR = {
  emerald: "bg-emerald-500", amber: "bg-amber-500", orange: "bg-orange-500",
  red: "bg-red-500", rose: "bg-rose-600",
};
const TEXT = {
  emerald: "text-emerald-700", amber: "text-amber-700", orange: "text-orange-700",
  red: "text-red-700", rose: "text-rose-700",
};
const BG = {
  emerald: "bg-emerald-50 border-emerald-100", amber: "bg-amber-50 border-amber-100",
  orange: "bg-orange-50 border-orange-100", red: "bg-red-50 border-red-100",
  rose: "bg-rose-50 border-rose-100",
};

export default function Invoices() {
  const { currentId } = useCompany();
  const fmtMoney = useMoneyFmt();
  const fmtDate = useDateFmt();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [itemsCatalog, setItemsCatalog] = useState([]);
  const [aging, setAging] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingPrefill, setCreatingPrefill] = useState(null);
  const [editing, setEditing] = useState(null);
  const [memorizing, setMemorizing] = useState(null);
  // Inline-edit state for invoice numbers on list rows.
  const [numEditId, setNumEditId] = useState(null);
  const [numEditVal, setNumEditVal] = useState("");
  // Client-side text search — filters against number/customer/status/amount.
  const [search, setSearch] = useState("");
  // Highlights ↔ A/R Aging ↔ Paid toggle — lifted here from ArAgingCard
  // so the parent's `filtered` memo can react to Paid mode.
  const [arView, setArView] = useState(() => {
    try { return localStorage.getItem("ar_aging_view") || "highlights"; }
    catch { return "highlights"; }
  });
  const setArViewPersist = (v) => {
    setArView(v);
    try { localStorage.setItem("ar_aging_view", v); } catch {}
  };
  const commitNumberEdit = async (inv) => {
    const val = (numEditVal || "").trim();
    if (!val || val === inv.number) { setNumEditId(null); return; }
    try {
      const r = await api.patch(`/companies/${currentId}/invoices/${inv.id}`, { number: val });
      if (r.data?.number_conflict) {
        toast.warning(`Heads up — another invoice already uses ${val}.`);
      } else {
        toast.success("Invoice number updated");
      }
      setNumEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  // Deep-link filters from Month Close: /invoices?outstanding=1&as_of=YYYY-MM-DD
  // Filtering is client-side against the already-loaded /invoices response
  // — matches the backend `_outstanding_count` semantics exactly
  // (issue_date <= as_of AND balance_due > 0).
  //
  // A/R Aging bucket click-through: /invoices?bucket=<key> where key is one
  // of `current | 1_30 | 31_60 | 61_90 | over_90`. Mirrors the backend
  // aging-report bucketing (balance_due > 0, days-late off today's date).
  const [params, setParams] = useSearchParams();
  const outstanding = params.get("outstanding") === "1";
  const overdueOnly = params.get("overdue") === "1";
  const asOf = params.get("as_of") || "";
  const bucket = params.get("bucket") || "";
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (arView === "paid") {
      // "Paid" mode — invoices already settled. Ignore aging/bucket/
      // outstanding/overdue URL filters; still respect search text.
      return items.filter(inv => {
        const isPaid = (inv.status === "paid") || (Number(inv.balance_due) <= 0.005);
        if (!isPaid) return false;
        if (q) {
          const hay = [inv.number, inv.contact_name, inv.customer_name, inv.status, inv.total, inv.balance_due]
            .map((v) => String(v ?? "").toLowerCase()).join(" ");
          if (!hay.includes(q)) return false;
        }
        return true;
      });
    }
    if (!outstanding && !asOf && !overdueOnly && !bucket && !q) return items;
    const today = new Date().toISOString().slice(0, 10);
    const daysLate = (due) => {
      if (!due) return -1; // no due date → treat as "not yet due"
      const ms = new Date(today).getTime() - new Date(due).getTime();
      return Math.floor(ms / 86400000);
    };
    return items.filter(inv => {
      if (outstanding && !(Number(inv.balance_due) > 0.005)) return false;
      if (overdueOnly) {
        if (!(Number(inv.balance_due) > 0.005)) return false;
        const due = inv.due_date || "";
        if (!due || due >= today) return false;
      }
      if (bucket) {
        if (!(Number(inv.balance_due) > 0.005)) return false;
        const dl = daysLate(inv.due_date);
        if (bucket === "current"  && !(dl <= 0)) return false;
        if (bucket === "1_30"     && !(dl >= 1  && dl <= 30)) return false;
        if (bucket === "31_60"    && !(dl >= 31 && dl <= 60)) return false;
        if (bucket === "61_90"    && !(dl >= 61 && dl <= 90)) return false;
        if (bucket === "over_90"  && !(dl >  90)) return false;
      }
      if (asOf) {
        const d = inv.issue_date || inv.date || "";
        if (d && d > asOf) return false;
      }
      if (q) {
        const hay = [
          inv.number, inv.contact_name, inv.customer_name, inv.status,
          inv.total, inv.balance_due,
        ].map((v) => String(v ?? "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, outstanding, asOf, overdueOnly, bucket, search, arView]);
  const clearFilters = () => {
    const p = new URLSearchParams(params);
    p.delete("outstanding"); p.delete("as_of"); p.delete("overdue"); p.delete("bucket");
    setParams(p, { replace: true });
  };
  const load = async () => {
    if (!currentId) return;
    const [i, c, a, it] = await Promise.all([
      api.get(`/companies/${currentId}/invoices`),
      api.get(`/companies/${currentId}/contacts`),
      api.get(`/companies/${currentId}/reports/ar-aging`),
      api.get(`/companies/${currentId}/items?usage=sales`),
    ]);
    setItems(i.data.invoices || []); setContacts(c.data.contacts || []); setAging(a.data);
    setItemsCatalog(it.data.items || []);
  };
  useEffect(() => { load(); }, [currentId]);

  // Voice-driven modal opener — see /lib/createBus.js.
  useCreateListener("invoice", (prefill) => {
    setCreatingPrefill(prefill || {});
    setCreating(true);
  });
  useActionListener("close-current-modal", () => {
    setCreating(false);
    setCreatingPrefill(null);
    setEditing(null);
    load();
  });
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/companies/${currentId}/invoices/${id}`);
    load();
  };
  return (
    <div className="space-y-4">
      <MonthCloseBreadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="text-slate-500 text-sm mt-1">Money in · sent to customers.</p>
        </div>
        <button data-testid={TID.addBtn} onClick={() => navigate("/invoices/new")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs">
          <Plus size={13} /> New Invoice
        </button>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Link to="/recurring" data-testid="recurring-link" className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
          <Repeat size={12} /> Recurring invoices
        </Link>
      </div>

      {aging && aging.total > 0 && (
        <ArAgingCard
          aging={aging}
          navigate={navigate}
          activeBucket={bucket}
          view={arView}
          setView={setArViewPersist}
          onBucketClick={(k) => {
            const p = new URLSearchParams(params);
            if (bucket === k) p.delete("bucket");
            else p.set("bucket", k);
            setParams(p, { replace: true });
          }}
        />
      )}
      <div className="rounded-xl border bg-white overflow-hidden">
        {/* Client-side search input — filters the invoices list by number,
             customer, status, or amount. Kept inside the table container
             so it visually anchors above the header row. */}
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/40">
          <div className="relative max-w-md">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoices — number, customer, status…"
              className="w-full pl-8 pr-8 py-1.5 rounded-md border border-slate-300 text-xs focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
              data-testid="invoices-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
                data-testid="invoices-search-clear"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        {(outstanding || asOf || overdueOnly || bucket) && (
          <div
            className="flex items-center justify-between px-3 py-2 bg-cyan-50 border-b border-cyan-100 text-xs text-cyan-900"
            data-testid="invoices-filter-chip"
          >
            <span>
              Showing{" "}
              {overdueOnly && <b>overdue</b>}
              {overdueOnly && outstanding && " · "}
              {outstanding && <b>outstanding</b>}
              {bucket && <b>{BUCKETS.find(b => b.key === bucket)?.label || bucket}</b>}
              {(outstanding || overdueOnly || bucket) && asOf && " "}
              {asOf && <>as of <b className="font-mono-num">{asOf}</b></>}
              {" "}·{" "}
              <span className="font-mono-num">{filtered.length}</span> of {items.length}
            </span>
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-cyan-700 hover:underline"
              data-testid="invoices-clear-filters"
            >
              <X size={12} /> Clear filters
            </button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b">
            <tr>
              <th className="px-3 py-2 text-left">Number</th>
              <th className="px-3 py-2 text-left">Customer</th>
              <th className="px-3 py-2 text-left">Due</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(inv => (
              <tr key={inv.id} className="border-b hover:bg-slate-50">
                <td className="px-3 py-2 font-mono-num text-slate-600">
                  {numEditId === inv.id ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        autoFocus
                        value={numEditVal}
                        onChange={(e) => setNumEditVal(e.target.value)}
                        onBlur={() => commitNumberEdit(inv)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitNumberEdit(inv);
                          if (e.key === "Escape") setNumEditId(null);
                        }}
                        className="w-28 border rounded px-1.5 py-0.5 text-xs font-mono-num"
                        data-testid={`invoice-number-input-${inv.id}`}
                      />
                      <Check size={12} className="text-emerald-600" />
                    </span>
                  ) : (
                    <button
                      onClick={() => { setNumEditId(inv.id); setNumEditVal(inv.number || ""); }}
                      className="text-slate-700 hover:text-indigo-600 hover:underline decoration-dotted"
                      title="Click to edit"
                      data-testid={`invoice-number-${inv.id}`}
                    >{inv.number}</button>
                  )}
                </td>
                <td className="px-3 py-2">{inv.contact_name}</td>
                <td className="px-3 py-2 font-mono-num text-slate-500">{fmtDate(inv.due_date)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{fmtMoney(inv.total)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{fmtMoney(inv.balance_due)}</td>
                <td className="px-3 py-2"><span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100">{inv.status}</span></td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`invoice-memorize-btn-${inv.id}`} onClick={() => setMemorizing(inv)}
                            title="Memorize (recurring)"
                            className="p-1 rounded hover:bg-fuchsia-100 text-fuchsia-600"><Repeat size={13} /></button>
                    <button data-testid="invoice-edit-btn" onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                            className="p-1 rounded hover:bg-indigo-100 text-indigo-600"><Pencil size={13} /></button>
                    <button onClick={() => del(inv.id)} className="p-1 rounded hover:bg-red-100 text-red-500"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-slate-500">
                  {(outstanding || asOf || overdueOnly || bucket)
                    ? `No matching invoices${items.length ? ` (${items.length} total, none met the filter)` : ""}.`
                    : "No invoices."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {creating && <InvoiceModal contacts={contacts} itemsCatalog={itemsCatalog} currentId={currentId} prefill={creatingPrefill}
                                  onClose={() => { setCreating(false); setCreatingPrefill(null); load(); }} />}
      {editing && <InvoiceModal contacts={contacts} itemsCatalog={itemsCatalog} currentId={currentId} invoice={editing} onClose={() => { setEditing(null); load(); }} />}
      {memorizing && <MemorizeModal currentId={currentId} source={memorizing} kind="invoice" onClose={() => setMemorizing(null)} />}
    </div>
  );
}

function InvoiceModal({ contacts, itemsCatalog, currentId, invoice, prefill, onClose }) {
  const fmtMoney = useMoneyFmt();
  const editMode = !!invoice;
  const p = prefill || {};
  const initLines = () => {
    if (invoice?.line_items?.length) return invoice.line_items.map(l => ({ ...l }));
    // Voice-driven: AI resolved specific catalog items. Trust the backend
    // enrichment (chat.py `_match_item`) to have hydrated item_id, rate,
    // income_account_*. Rows with rate=0 are cache-misses the user can
    // fill in — keep the parsed quantity + name so nothing is lost.
    if (Array.isArray(p.lines) && p.lines.length) {
      return p.lines.map(l => {
        const quantity = Number(l.quantity || 1) || 1;
        const rate = Number(l.rate || 0);
        return {
          item_id: l.item_id || undefined,
          item_name: l.item_name || undefined,
          description: l.description || l.item_name || "",
          quantity,
          rate,
          amount: quantity * rate,
          income_account_id: l.income_account_id || undefined,
          income_account_name: l.income_account_name || undefined,
        };
      });
    }
    if (p.amount || p.description) {
      const amt = Number(p.amount || 0);
      return [{
        description: p.description || "Services",
        quantity: 1,
        rate: amt,
        amount: amt,
      }];
    }
    return [{ description: "", quantity: 1, rate: 0, amount: 0 }];
  };
  const [contact, setContact] = useState(invoice?.contact_id || p.contact_id || "");
  // Race guard: prefill.contact_id may arrive before the parent's contacts
  // list finishes loading (voice-command flow). Re-apply if it starts
  // matching a real option once contacts populate.
  useEffect(() => {
    if (!contact && p.contact_id && contacts.some(c => c.id === p.contact_id)) {
      setContact(p.contact_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, p.contact_id]);
  // Resolve the invoice/due dates with three rules:
  //  1. `due_days === 0` (spoken "due today") must NOT be swallowed by
  //     the `|| 30` fallback — check for undefined/null explicitly.
  //  2. If the resolved due date lands BEFORE today, backdate the invoice
  //     date to match so the invoice isn't stamped as already-overdue.
  //  3. In edit mode, always trust the persisted values.
  const _todayISO = new Date().toISOString().slice(0, 10);
  const _dueDaysRaw = p.due_days;
  const _dueOffset = (_dueDaysRaw === undefined || _dueDaysRaw === null || _dueDaysRaw === "")
    ? 30 : Number(_dueDaysRaw);
  const _dueDefault = new Date(Date.now() + _dueOffset * 86400000).toISOString().slice(0, 10);
  const _resolvedDue = invoice?.due_date || p.due_date || _dueDefault;
  const _resolvedIssue = invoice?.issue_date
    || p.issue_date
    || (_resolvedDue < _todayISO ? _resolvedDue : _todayISO);
  const [issue, setIssue] = useState(_resolvedIssue);
  const [due, setDue] = useState(_resolvedDue);
  const [lines, setLines] = useState(initLines);
  const [tax, setTax] = useState(invoice?.tax || Number(p.tax || 0));
  const [status, setStatus] = useState(invoice?.status || p.status || "sent");
  const [number, setNumber] = useState(invoice?.number || "");
  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  useEffect(() => {
    if (mode !== "preview" || !editMode || !invoice?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/companies/${currentId}/invoices/${invoice.id}/pdf`, { responseType: "blob" });
        if (cancelled) return;
        setPdfBlobUrl(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
      } catch (e) { toast.error("Could not load preview"); }
    })();
    return () => { cancelled = true; if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, invoice?.id, invoice?.updated_at]);
  const upd = (i, patch) => setLines(lines.map((x, j) => j === i ? { ...x, ...patch, amount: (patch.quantity !== undefined ? patch.quantity : x.quantity) * (patch.rate !== undefined ? patch.rate : x.rate) } : x));
  const total = lines.reduce((s, l) => s + Number(l.amount || 0), 0) + Number(tax);
  const save = async () => {
    const c = contacts.find(x => x.id === contact);
    const body = {
      contact_id: contact || null, contact_name: c?.name || invoice?.contact_name || "",
      issue_date: issue, due_date: due, line_items: lines, tax: Number(tax), status,
    };
    if (number && number !== invoice?.number) body.number = number.trim();
    if (editMode) {
      const r = await api.patch(`/companies/${currentId}/invoices/${invoice.id}`, body);
      if (r.data?.number_conflict) {
        toast.warning(`Heads up — another invoice already uses ${body.number}.`);
      } else {
        toast.success("Invoice updated");
      }
    } else {
      if (number) body.number = number.trim();
      await api.post(`/companies/${currentId}/invoices`, body);
      toast.success("Invoice created");
    }
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-auto p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-heading font-semibold">{editMode ? `Edit Invoice ${invoice.number}` : "New Invoice"}</h3>
            {editMode && (
              <div className="inline-flex rounded-md border bg-slate-50 p-0.5 text-xs" data-testid="invoice-view-toggle">
                <button
                  type="button"
                  onClick={() => setMode("edit")}
                  data-testid="invoice-mode-edit"
                  className={`px-2.5 py-1 rounded ${mode === "edit" ? "bg-slate-900 text-white" : "text-slate-600"}`}
                >Edit</button>
                <button
                  type="button"
                  onClick={() => setMode("preview")}
                  data-testid="invoice-mode-preview"
                  className={`px-2.5 py-1 rounded ${mode === "preview" ? "bg-emerald-600 text-white" : "text-slate-600"}`}
                >Preview</button>
              </div>
            )}
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        {mode === "preview" ? (
          pdfBlobUrl ? (
            <iframe title="Invoice preview" src={pdfBlobUrl} className="w-full h-[70vh] border rounded" data-testid="invoice-preview-iframe" />
          ) : (
            <div className="h-[60vh] flex items-center justify-center text-slate-400 text-sm">Loading preview…</div>
          )
        ) : (<>
        <div className="grid grid-cols-4 gap-2">
          <select value={contact} onChange={(e) => setContact(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            <option value="">Customer…</option>
            {contacts.filter(c => c.type === "customer" || c.type === "both").map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            <option value="draft">Draft</option><option value="sent">Sent</option>
            <option value="partial">Partial</option><option value="paid">Paid</option>
          </select>
        </div>
        <div>
          <label htmlFor={`inv-num-${invoice?.id || "new"}`}
                 className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">
            Invoice number
          </label>
          <input
            id={`inv-num-${invoice?.id || "new"}`}
            key={`inv-num-${invoice?.id || "new"}`}
            type="text"
            autoComplete="off"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="e.g. INV-1001 (leave blank to auto-assign)"
            className="w-full border rounded px-2 py-1.5 text-sm font-mono-num focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
            data-testid="invoice-modal-number"
          />
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <div className="col-span-5">
                <ItemPicker
                  items={itemsCatalog}
                  value={l.description}
                  onChangeText={(txt) => upd(i, { description: txt })}
                  onPickItem={(it) => upd(i, {
                    item_id: it.id,
                    item_name: it.name,
                    // Item name on the line — the catalog `description`
                    // is internal (cost notes / SKU details) and stays
                    // out of the customer-facing invoice. User can
                    // still overwrite inline.
                    description: it.name,
                    rate: Number(it.price || 0),
                    income_account_id: it.income_account_id || null,
                    income_account_name: it.income_account_name || "",
                    category: it.income_account_name || "",
                    // Item's default sales tax carried through as a
                    // hint — the simpler Invoices.jsx doesn't render
                    // per-line tax UI, so this only matters if the
                    // record gets edited later in the fuller
                    // InvoiceEditor (which resolves id → rate on load).
                    ...(it.tax_rate_id ? {
                      tax_id:   it.tax_rate_id,
                      tax_name: it.tax_rate_name || "",
                    } : {}),
                  })}
                  testId={`invoice-line-${i}`}
                />
              </div>
              <input type="number" value={l.quantity} onChange={(e) => upd(i, { quantity: Number(e.target.value) })} className="col-span-2 border rounded px-2 py-1.5 text-sm font-mono-num" />
              <input type="number" value={l.rate} onChange={(e) => upd(i, { rate: Number(e.target.value) })} className="col-span-2 border rounded px-2 py-1.5 text-sm font-mono-num" />
              <div className="col-span-2 py-1.5 text-right font-mono-num">{fmtMoney(l.amount)}</div>
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="col-span-1 text-red-500"><Trash2 size={13} /></button>
            </div>
          ))}
          <button onClick={() => setLines([...lines, { description: "", quantity: 1, rate: 0, amount: 0 }])}
                  className="text-xs text-slate-600 border border-dashed rounded px-2 py-1">+ Line</button>
        </div>
        <div className="flex justify-end gap-4 items-center border-t pt-3">
          <div className="text-sm">Tax: <input type="number" value={tax} onChange={(e) => setTax(e.target.value)} className="w-24 border rounded px-2 py-1 text-sm font-mono-num" /></div>
          <div className="text-lg font-mono-num font-semibold">Total: {fmtMoney(total)}</div>
          <button data-testid={TID.saveBtn} onClick={save} className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm">
            {editMode ? "Save changes" : "Save Invoice"}
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
}


/**
 * A/R Aging card with a top-of-card toggle between:
 *   • "A/R Aging"   — canonical 5-bucket view (Current / 1-30 / 31-60 / 61-90 / 90+)
 *   • "Highlights"  — two glanceable cards (Overdue + Due within 30 days)
 *                     plus a big AI Follow-up call-to-action.
 *
 * Bucket vocabulary:
 *   overdue           = 1_30 + 31_60 + 61_90 + over_90
 *   due within 30 days = the "current" bucket (not yet due, Net 30 default)
 */
function ArAgingCard({ aging, navigate, activeBucket = "", onBucketClick, view, setView }) {
  const fmtMoney = useMoneyFmt();
  // View state now controlled by parent so its `filtered` memo can react
  // to "paid" mode. Keep a local fallback for standalone use just in
  // case, mirroring the persisted default.
  const [localView, setLocalView] = useState(() => {
    try { return localStorage.getItem("ar_aging_view") || "highlights"; }
    catch { return "highlights"; }
  });
  const effectiveView = view ?? localView;
  const setViewPersist = (v) => {
    if (setView) return setView(v);
    setLocalView(v);
    try { localStorage.setItem("ar_aging_view", v); } catch {}
  };
  const [aiOpen, setAiOpen] = useState(false);
  const { currentId } = useCompany();
  const overdue = ["1_30", "31_60", "61_90", "over_90"]
    .reduce((s, k) => s + (aging.buckets[k] || 0), 0);
  const dueSoon = aging.buckets.current || 0;
  const atRisk = (aging.buckets["61_90"] || 0) + (aging.buckets["over_90"] || 0);

  return (
    <div data-testid="ar-aging-widget" className="rounded-xl border bg-white p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold">
            {effectiveView === "paid" ? "Paid" : effectiveView === "aging" ? "A/R Aging" : "Highlights"}
          </div>
          <div className="text-xs text-slate-500">
            {effectiveView === "paid"
              ? <>Invoices already settled</>
              : <>As of {aging.as_of} · <span className="font-mono-num font-semibold text-slate-800">{fmtMoney(aging.total)}</span> outstanding</>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {atRisk > 0 && effectiveView === "aging" && (
            <div className="text-xs px-2 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 flex items-center gap-1">
              <AlertTriangle size={12} />
              {fmtMoney(atRisk)} at collection risk
            </div>
          )}
          {/* Toggle — Highlights / A/R Aging / Paid */}
          <div className="inline-flex rounded-md border overflow-hidden text-xs bg-slate-50" data-testid="ar-aging-toggle">
            <button
              onClick={() => setViewPersist("highlights")}
              className={`px-3 py-1.5 ${effectiveView === "highlights" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`}
              data-testid="ar-aging-toggle-highlights"
            >Highlights</button>
            <button
              onClick={() => setViewPersist("aging")}
              className={`px-3 py-1.5 ${effectiveView === "aging" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`}
              data-testid="ar-aging-toggle-aging"
            >A/R Aging</button>
            <button
              onClick={() => setViewPersist("paid")}
              className={`px-3 py-1.5 ${effectiveView === "paid" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-white"}`}
              data-testid="ar-aging-toggle-paid"
            >Paid</button>
          </div>
        </div>
      </div>

      {effectiveView === "paid" ? null : effectiveView === "aging" ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {BUCKETS.map(b => {
              const amt = aging.buckets[b.key] || 0;
              const pct = aging.total ? (amt / aging.total) * 100 : 0;
              const isActive = activeBucket === b.key;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => onBucketClick && onBucketClick(b.key)}
                  className={`text-left rounded-lg border p-3 transition ${BG[b.color]} hover:shadow-md hover:-translate-y-0.5 ${isActive ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                  data-testid={`ar-aging-bucket-${b.key}`}
                  aria-pressed={isActive}
                  title={isActive ? "Click again to clear filter" : `Filter invoices to ${b.label}`}
                >
                  <div className={`text-[10px] uppercase tracking-wider font-semibold ${TEXT[b.color]}`}>{b.label}</div>
                  <div className={`font-mono-num text-lg font-semibold mt-0.5 ${TEXT[b.color]}`}>{fmtMoney(amt)}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{b.desc}</div>
                  <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full ${BAR[b.color]} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className={`text-[10px] mt-1 ${TEXT[b.color]}`}>{pct.toFixed(0)}% of A/R{isActive ? " · filtered" : ""}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-4">
            <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
              {BUCKETS.map(b => {
                const amt = aging.buckets[b.key] || 0;
                const pct = aging.total ? (amt / aging.total) * 100 : 0;
                if (pct === 0) return null;
                return (
                  <div key={b.key} className={BAR[b.color]} style={{ width: `${pct}%` }} title={`${b.label}: ${fmtMoney(amt)}`} />
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ar-highlights">
          <button
            type="button"
            onClick={() => navigate("/invoices?overdue=1")}
            className="text-left rounded-lg border-2 border-red-200 bg-red-50 p-4 hover:bg-red-100 transition"
            data-testid="ar-highlights-overdue-card"
          >
            <div className="text-[11px] uppercase tracking-wider font-semibold text-red-700">Overdue · click to filter</div>
            <div className="font-mono-num text-2xl font-bold mt-1 text-red-700" data-testid="ar-highlights-overdue">
              {fmtMoney(overdue)}
            </div>
            <div className="text-xs text-red-600/80 mt-1">Past-due invoices need attention.</div>
          </button>
          <button
            type="button"
            onClick={() => navigate("/invoices?bucket=current")}
            className="text-left rounded-lg border-2 border-amber-200 bg-amber-50 p-4 hover:bg-amber-100 transition"
            data-testid="ar-highlights-due-soon-card"
          >
            <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">Due within 30 days · click to filter</div>
            <div className="font-mono-num text-2xl font-bold mt-1 text-amber-700" data-testid="ar-highlights-due-soon">
              {fmtMoney(dueSoon)}
            </div>
            <div className="text-xs text-amber-700/80 mt-1">Not yet late — send a friendly nudge.</div>
          </button>
          <button
            type="button"
            onClick={() => setAiOpen(true)}
            className="rounded-lg border-2 border-indigo-400 bg-gradient-to-br from-indigo-600 to-indigo-700 p-4 text-white text-left shadow-md hover:shadow-lg hover:from-indigo-500 hover:to-indigo-600 transition"
            data-testid="ar-highlights-ai-followup"
          >
            <div className="text-[11px] uppercase tracking-wider font-semibold text-indigo-100 inline-flex items-center gap-1.5">
              <Sparkles size={12} /> AI Follow-up
            </div>
            <div className="text-lg font-bold mt-1">
              {overdue > 0 ? `Draft ${overdue > 1 ? "chase emails" : "chase email"}` : "Prep next-run nudges"}
            </div>
            <div className="text-xs text-indigo-100/90 mt-1">
              One-tap AI-drafted follow-ups for every overdue customer.
            </div>
          </button>
        </div>
      )}
      {aiOpen && <AIFollowupModal currentId={currentId} onClose={() => setAiOpen(false)} />}
    </div>
  );
}


/**
 * AIFollowupModal — one-tap AI-drafted chase emails for every overdue
 * customer. Fetches per-customer drafts from
 * `POST /companies/{cid}/invoices/ai-followup/drafts` (grouped by
 * contact, one email per customer), lets the user preview/edit each
 * subject + body, toggle which rows to send, then dispatches via
 * `POST /companies/{cid}/invoices/ai-followup/send-all`.
 *
 * Skipped rows (no email on file, unchecked) are surfaced in the send
 * summary so the pro can act on them.
 */
export function AIFollowupModal({ currentId, onClose }) {
  const fmtMoney = useMoneyFmt();
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState({});        // idx -> bool
  const [expanded, setExpanded] = useState(0);         // idx currently expanded
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);          // {sent, failed, skipped, total}
  const [showFollowed, setShowFollowed] = useState(false); // collapsed by default

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(`/companies/${currentId}/invoices/ai-followup/drafts`, {});
        if (cancelled) return;
        const list = (r.data?.drafts || []).map(d => ({ ...d, saved_email: d.to_email || "" }));
        setDrafts(list);
        // Default: pre-select customers who have an email AND weren't
        // chased in the last 7 days (prevents accidental double-nudge).
        const sel = {};
        list.forEach((d, i) => {
          const hasEmail = !!(d.to_email && d.to_email.includes("@"));
          sel[i] = hasEmail && !d.recently_followed_up;
        });
        setSelected(sel);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not draft follow-ups");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentId]);

  const upd = (i, patch) => setDrafts(prev => prev.map((d, j) => j === i ? { ...d, ...patch } : d));

  // Persist a typed email back onto the underlying contact record so
  // future drafts (and the auto scheduler) can find it. Uses the
  // canonical PATCH /contacts/{id} endpoint.
  const saveEmailToCustomer = async (i) => {
    const d = drafts[i];
    const clean = (d.to_email || "").trim();
    if (!clean || !clean.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!d.customer_id) {
      toast.error("No linked customer to save this email to.");
      return;
    }
    upd(i, { _savingEmail: true });
    try {
      await api.patch(`/companies/${currentId}/contacts/${d.customer_id}`, { email: clean });
      upd(i, { _savingEmail: false, _emailSaved: true, saved_email: clean });
      toast.success(`Saved email for ${d.customer_name}`);
    } catch (e) {
      upd(i, { _savingEmail: false });
      toast.error(e.response?.data?.detail || "Could not save email");
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const missingEmailCount = drafts.filter(d => !d.to_email || !d.to_email.includes("@")).length;

  // Split into fresh (never chased in last 7d) vs recently-followed-up.
  const freshIdx = drafts.map((d, i) => i).filter(i => !drafts[i].recently_followed_up);
  const recentIdx = drafts.map((d, i) => i).filter(i => drafts[i].recently_followed_up);

  const renderRow = (i) => {
    const d = drafts[i];
    const hasEmail = d.to_email && d.to_email.includes("@");
    const isOpen = expanded === i;
    const recent = !!d.recently_followed_up;
    return (
      <div key={i} className={`${selected[i] ? "bg-white" : "bg-slate-50"} ${recent ? "border-l-4 border-l-amber-400" : ""}`} data-testid={`ai-followup-row-${i}`}>
        <div className="px-5 py-3 flex items-center gap-3">
          <input
            type="checkbox"
            checked={!!selected[i]}
            onChange={(e) => setSelected(s => ({ ...s, [i]: e.target.checked }))}
            disabled={!hasEmail}
            className="h-4 w-4 accent-indigo-600 disabled:opacity-40"
            data-testid={`ai-followup-select-${i}`}
            title={hasEmail ? "" : "No email on file"}
          />
          <button
            type="button"
            className="flex-1 text-left"
            onClick={() => setExpanded(isOpen ? -1 : i)}
            data-testid={`ai-followup-expand-${i}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold text-slate-800">{d.customer_name}</span>
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                {d.oldest_days}d late
              </span>
              {recent && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                  data-testid={`ai-followup-recent-badge-${i}`}
                  title="Chased recently — sending again may feel like spam"
                >
                  <Clock size={10} />
                  {d.followup_days_ago === 0 ? "chased today" : `chased ${d.followup_days_ago}d ago`}
                </span>
              )}
              <span className="font-mono-num text-slate-700 text-sm">{fmtMoney(d.total_due)}</span>
              <span className="text-xs text-slate-500">
                · {d.invoice_ids?.length || 0} invoice{(d.invoice_ids?.length || 0) > 1 ? "s" : ""}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              To: {hasEmail ? <span className="font-mono-num">{d.to_email}</span> : <span className="text-amber-700">no email on file</span>}
            </div>
          </button>
        </div>
        {isOpen && (
          <div className="px-5 pb-4 space-y-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">To</label>
              <div className="flex items-center gap-2">
                <input
                  value={d.to_email || ""}
                  onChange={(e) => upd(i, { to_email: e.target.value, _emailSaved: false })}
                  placeholder="customer@example.com"
                  className="flex-1 border rounded px-2 py-1.5 text-sm font-mono-num"
                  data-testid={`ai-followup-to-${i}`}
                />
                {d.customer_id && (d.to_email || "").includes("@") && (d.to_email || "").trim() !== (d.saved_email || "").trim() && (
                  <button
                    onClick={() => saveEmailToCustomer(i)}
                    disabled={d._savingEmail}
                    className="text-[11px] px-2 py-1.5 rounded border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-40 inline-flex items-center gap-1 shrink-0"
                    data-testid={`ai-followup-save-email-${i}`}
                    title="Persist this address to the customer record so future auto follow-ups can send"
                  >
                    {d._savingEmail ? <Loader2 size={11} className="animate-spin" /> : null}
                    Save to customer
                  </button>
                )}
                {d._emailSaved && (
                  <span className="text-[11px] text-emerald-700 inline-flex items-center gap-0.5 shrink-0" data-testid={`ai-followup-email-saved-${i}`}>
                    <Check size={11} /> Saved
                  </span>
                )}
              </div>
              {!hasEmail && (
                <div className="text-[11px] text-amber-700 mt-1" data-testid={`ai-followup-no-email-warn-${i}`}>
                  No email on file for <b>{d.customer_name}</b>. Type one above and click <b>Save to customer</b> to enable this row.
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Subject</label>
              <input
                value={d.subject || ""}
                onChange={(e) => upd(i, { subject: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
                data-testid={`ai-followup-subject-${i}`}
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Body</label>
              <textarea
                value={d.body || ""}
                onChange={(e) => upd(i, { body: e.target.value })}
                rows={10}
                className="w-full border rounded px-2 py-1.5 text-sm font-mono-num leading-relaxed"
                data-testid={`ai-followup-body-${i}`}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const sendAll = async () => {
    const payload = drafts
      .map((d, i) => ({ ...d, __i: i }))
      .filter(d => selected[d.__i])
      .map(({ __i, ...d }) => d);
    if (!payload.length) { toast.warning("Nothing to send — select at least one draft."); return; }
    setSending(true);
    try {
      const r = await api.post(`/companies/${currentId}/invoices/ai-followup/send-all`, { drafts: payload });
      setResult(r.data);
      const { sent = 0, failed = 0, skipped = [] } = r.data || {};
      if (sent && !failed && !skipped.length) toast.success(`Sent ${sent} follow-up${sent > 1 ? "s" : ""}`);
      else if (sent) toast.success(`Sent ${sent} · ${failed} failed · ${skipped.length} skipped`);
      else toast.error("Nothing was sent — check email deliverability.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ai-followup-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
          <div className="flex items-center gap-2">
            <Sparkles size={16} />
            <h3 className="font-heading font-semibold">AI Follow-up · Drafted chase emails</h3>
          </div>
          <button onClick={onClose} data-testid="ai-followup-close" className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-500 py-16" data-testid="ai-followup-loading">
            <Loader2 size={28} className="animate-spin text-indigo-500" />
            <div className="text-sm">Drafting personalised emails for every overdue customer…</div>
          </div>
        ) : drafts.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-500 py-16" data-testid="ai-followup-empty">
            <Check size={28} className="text-emerald-500" />
            <div className="font-heading font-semibold text-slate-800">All clear!</div>
            <div className="text-sm">No overdue invoices to chase right now.</div>
          </div>
        ) : (
          <>
            <div className="px-5 py-2 border-b bg-slate-50 text-xs text-slate-600 flex items-center justify-between">
              <div>
                <span className="font-semibold text-slate-800">{drafts.length}</span> customer{drafts.length > 1 ? "s" : ""} with overdue balances ·{" "}
                <span className="font-semibold text-indigo-700">{selectedCount}</span> selected
                {recentIdx.length > 0 && (
                  <span className="inline-flex items-center gap-1 ml-2 text-amber-700" data-testid="ai-followup-recent-count">
                    <Clock size={11} /> {recentIdx.length} chased in last 7 days
                  </span>
                )}
                {missingEmailCount > 0 && (
                  <span className="inline-flex items-center gap-1 ml-2 text-amber-700">
                    <MailWarning size={11} /> {missingEmailCount} missing email
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  // Toggle only the "fresh" rows — recently-followed-up
                  // stay untouched so the user has to opt into re-nudging.
                  const allOn = freshIdx.every(i => selected[i]);
                  const next = { ...selected };
                  freshIdx.forEach(i => {
                    const d = drafts[i];
                    next[i] = allOn ? false : !!(d.to_email && d.to_email.includes("@"));
                  });
                  setSelected(next);
                }}
                className="text-indigo-600 hover:underline"
                data-testid="ai-followup-toggle-all"
              >Toggle all</button>
            </div>

            <div className="flex-1 overflow-auto divide-y">
              {freshIdx.length === 0 && recentIdx.length > 0 && (
                <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="ai-followup-all-chased">
                  Every overdue customer has been chased in the last 7 days. Expand below to override.
                </div>
              )}
              {freshIdx.map(i => renderRow(i))}
              {recentIdx.length > 0 && (
                <div className="bg-amber-50/40" data-testid="ai-followup-recent-section">
                  <button
                    type="button"
                    onClick={() => setShowFollowed(v => !v)}
                    className="w-full flex items-center gap-2 px-5 py-2.5 text-xs font-semibold text-amber-900 hover:bg-amber-50"
                    data-testid="ai-followup-recent-toggle"
                  >
                    {showFollowed ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Clock size={12} />
                    Recently followed up · {recentIdx.length}
                    <span className="font-normal text-amber-700/80 ml-1">(chased in last 7 days — expand to re-nudge)</span>
                  </button>
                  {showFollowed && recentIdx.map(i => renderRow(i))}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {result ? (
                  <span data-testid="ai-followup-result">
                    <b className="text-emerald-700">{result.sent} sent</b>
                    {result.failed > 0 && <span className="text-red-700"> · {result.failed} failed</span>}
                    {result.skipped?.length > 0 && <span className="text-amber-700"> · {result.skipped.length} skipped</span>}
                  </span>
                ) : (
                  <span>Emails send via your firm's connected inbox.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="px-3 py-1.5 rounded-md border text-sm text-slate-600 hover:bg-white" data-testid="ai-followup-cancel">
                  Close
                </button>
                <button
                  onClick={sendAll}
                  disabled={sending || selectedCount === 0}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-500 disabled:opacity-50"
                  data-testid="ai-followup-send-all"
                >
                  {sending ? <><Loader2 size={13} className="animate-spin" /> Sending…</> : <><Send size={13} /> Send {selectedCount || ""} follow-up{selectedCount === 1 ? "" : "s"}</>}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

